import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

interface MonthYearPickerProps {
  selectedDate: Date;
  onDateChange: (date: Date) => void;
}

const MONTHS = [
  'Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun',
  'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'
];

export const MonthYearPicker = ({ selectedDate, onDateChange }: MonthYearPickerProps) => {
  const [showMonthSelector, setShowMonthSelector] = useState(false);
  const [selectorYear, setSelectorYear] = useState(selectedDate.getFullYear());
  
  const selectedMonth = selectedDate.getMonth();
  const selectedYear = selectedDate.getFullYear();

  // Navegação mês a mês no controle principal
  const handlePreviousMonth = () => {
    if (selectedMonth === 0) {
      // Janeiro -> Dezembro do ano anterior
      const newDate = new Date(selectedYear - 1, 11, 1);
      onDateChange(newDate);
    } else {
      const newDate = new Date(selectedYear, selectedMonth - 1, 1);
      onDateChange(newDate);
    }
  };

  const handleNextMonth = () => {
    if (selectedMonth === 11) {
      // Dezembro -> Janeiro do próximo ano
      const newDate = new Date(selectedYear + 1, 0, 1);
      onDateChange(newDate);
    } else {
      const newDate = new Date(selectedYear, selectedMonth + 1, 1);
      onDateChange(newDate);
    }
  };

  // Navegação ano a ano no seletor
  const handlePreviousYear = () => {
    setSelectorYear(prev => prev - 1);
  };

  const handleNextYear = () => {
    setSelectorYear(prev => prev + 1);
  };

  // Seleção de mês
  const handleMonthSelect = (month: number) => {
    const newDate = new Date(selectorYear, month, 1);
    onDateChange(newDate);
    setShowMonthSelector(false);
  };

  // Abrir seletor
  const handleOpenSelector = () => {
    setSelectorYear(selectedYear);
    setShowMonthSelector(true);
  };

  if (showMonthSelector) {
    return (
      <div className="space-y-4">
        {/* Layout Horizontal com Navegação de Ano e Meses */}
        <div className="flex items-center justify-center gap-2">
          {/* Botão Ano Anterior */}
          <Button
            variant="ghost"
            size="sm"
            onClick={handlePreviousYear}
            className="h-8 px-2"
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          
          {/* Ano */}
          <div className="text-base font-semibold min-w-[60px] text-center">
            {selectorYear}
          </div>
          
          {/* Botão Próximo Ano */}
          <Button
            variant="ghost"
            size="sm"
            onClick={handleNextYear}
            className="h-8 px-2"
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>

        {/* Linha Horizontal de Meses */}
        <div className="flex items-center justify-center gap-1 flex-wrap">
          {MONTHS.map((monthName, index) => {
            const isSelected = index === selectedMonth && selectorYear === selectedYear;
            return (
              <Button
                key={index}
                variant="ghost"
                onClick={() => handleMonthSelect(index)}
                className={`
                  h-8 px-3 text-sm rounded-full font-medium transition-all border
                  ${isSelected 
                    ? 'bg-primary text-primary-foreground border-primary' 
                    : 'border-border hover:bg-accent hover:text-accent-foreground'
                  }
                `}
              >
                {monthName}
              </Button>
            );
          })}
        </div>

        {/* Botão Fechar */}
        <div className="flex justify-center">
          <Button
            variant="ghost"
            onClick={() => setShowMonthSelector(false)}
            className="text-muted-foreground hover:text-foreground text-sm"
          >
            Fechar
          </Button>
        </div>
      </div>
    );
  }

  // Controle Principal
  return (
    <div className="flex items-center justify-center gap-4">
      <Button
        variant="ghost"
        size="sm"
        onClick={handlePreviousMonth}
        className="h-10 w-10 p-0 rounded-full border border-border hover:bg-accent hover:border-accent"
      >
        <ChevronLeft className="h-4 w-4" />
      </Button>
      
      <button
        onClick={handleOpenSelector}
        className="text-base font-semibold text-center min-w-[160px] h-10 px-4 rounded-full border border-border hover:bg-accent hover:border-accent hover:text-accent-foreground transition-all cursor-pointer"
      >
        {format(selectedDate, 'MMMM yyyy', { locale: ptBR })}
      </button>
      
      <Button
        variant="ghost"
        size="sm"
        onClick={handleNextMonth}
        className="h-10 w-10 p-0 rounded-full border border-border hover:bg-accent hover:border-accent"
      >
        <ChevronRight className="h-4 w-4" />
      </Button>
    </div>
  );
};